import axios from 'axios';
import {
  CompanyProfileResponse,
  UpdateCompanyProfileRequest,
  UpdateCompanyProfileResponse,
  CompanyProfileImageUploadResponse,
  CompanyLogoUploadResponse,
  CompanyBasicInfo,
  CompanyProfile,
  CompanyProfilePermissions,
} from '../types/company-profile';

class CompanyProfileService {
  /**
   * エラーハンドリング
   */
  private handleAxiosError(error: any): never {
    if (axios.isAxiosError(error)) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }

      // HTTPステータスコードに基づくエラーメッセージ
      switch (error.response?.status) {
        case 400:
          throw new Error('入力データに問題があります');
        case 401:
          throw new Error('認証が必要です。ログインしてください');
        case 403:
          throw new Error('この操作の権限がありません');
        case 404:
          throw new Error('企業が見つかりません');
        case 500:
          throw new Error('サーバーエラーが発生しました');
        default:
          throw new Error(`API エラー: ${error.response?.status || 'unknown'}`);
      }
    }

    throw new Error('企業プロフィールの処理に失敗しました');
  }

  /**
   * 企業プロフィール取得
   */
  async getCompanyProfile(companyId: number): Promise<{
    company: CompanyBasicInfo;
    profile: CompanyProfile;
    permissions: CompanyProfilePermissions;
  }> {
    try {
      const response = await axios.get<CompanyProfileResponse>(
        `/api/companies/${companyId}/profile`,
        { withCredentials: true }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to get company profile:', error);
      this.handleAxiosError(error);
    }
  }

  /**
   * 企業プロフィール更新
   */
  async updateCompanyProfile(
    companyId: number,
    profileData: UpdateCompanyProfileRequest
  ): Promise<UpdateCompanyProfileResponse['data']> {
    try {
      const response = await axios.put<UpdateCompanyProfileResponse>(
        `/api/companies/${companyId}/profile`,
        profileData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to update company profile:', error);
      this.handleAxiosError(error);
    }
  }

  /**
   * 企業プロフィール画像アップロード
   */
  async uploadCompanyProfileImage(
    companyId: number,
    file: File
  ): Promise<CompanyProfileImageUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post<CompanyProfileImageUploadResponse>(
        `/api/companies/${companyId}/profile/images`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to upload company profile image:', error);
      this.handleAxiosError(error);
    }
  }

  /**
   * 企業ロゴアップロード
   */
  async uploadCompanyLogo(
    companyId: number,
    file: File
  ): Promise<CompanyLogoUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await axios.post<CompanyLogoUploadResponse>(
        `/api/companies/${companyId}/profile/logo`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to upload company logo:', error);
      this.handleAxiosError(error);
    }
  }
}

export const companyProfileService = new CompanyProfileService();
